import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { CaretDown, ShieldCheck } from "@phosphor-icons/react";
import VerdictBadge from "./VerdictBadge";
import SourceItem from "./SourceItem";
import { isFactCheckerSource } from "@/lib/factCheckers";

export default function ClaimCard({ claim, index, factCheckersOnly = false }) {
  const [expanded, setExpanded] = useState(false);

  const allSources = claim.sources || [];
  const visibleSources = useMemo(
    () => (factCheckersOnly ? allSources.filter(isFactCheckerSource) : allSources),
    [allSources, factCheckersOnly]
  );
  const fcCount = useMemo(() => allSources.filter(isFactCheckerSource).length, [allSources]);

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.04 }}
      data-testid={`claim-card-${index}`}
      className="border border-zinc-300 bg-white p-4 md:p-5 hover-brutal"
    >
      {/* Top row: index + verdict badge (verdict already shows confidence) */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="font-mono-plex text-[10px] font-semibold text-zinc-500">
          CLAIM {String(index + 1).padStart(2, "0")}
        </span>
        <VerdictBadge verdict={claim.verdict} size="md" confidence={claim.confidence} />
      </div>

      {/* Claim text + single-line explanation stacked tightly */}
      <blockquote className="font-display text-[17px] md:text-lg text-zinc-950 leading-snug mb-1.5">
        &ldquo;{claim.original_quote}&rdquo;
      </blockquote>
      {claim.short_explanation && (
        <p className="text-[13px] italic text-zinc-600 leading-relaxed mb-3">
          {claim.short_explanation}
        </p>
      )}

      {/* Sources toggle — respects fact-checker filter */}
      <button
        onClick={() => setExpanded((v) => !v)}
        data-testid={`toggle-sources-${index}`}
        className="w-full flex items-center justify-between border-t border-zinc-200 pt-2.5 group"
      >
        <span className="font-mono-plex text-[10px] font-semibold text-zinc-900 flex items-center gap-1.5">
          {factCheckersOnly && (
            <ShieldCheck weight="fill" className="w-3 h-3 text-emerald-600" />
          )}
          {expanded ? "HIDE" : "SHOW"} · {visibleSources.length} SOURCE{visibleSources.length === 1 ? "" : "S"}
          {factCheckersOnly && fcCount === 0 && (
            <span className="text-amber-700 font-normal normal-case tracking-normal ml-1">no fact-checker match</span>
          )}
        </span>
        <CaretDown
          weight="bold"
          className={`w-3.5 h-3.5 text-zinc-900 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {expanded && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="mt-2.5 space-y-2"
        >
          {visibleSources.length === 0 && (
            <p className="text-[12.5px] text-zinc-500 italic">
              {factCheckersOnly
                ? "No IFCN fact-checker sources for this claim. Turn the filter off to see other authoritative sources."
                : "No authoritative sources returned."}
            </p>
          )}
          {visibleSources.map((s, i) => (
            <SourceItem key={s.url || i} source={s} index={i} />
          ))}

          {claim.detailed_reasoning && !factCheckersOnly && (
            <div className="mt-3 p-3 bg-zinc-50 border border-zinc-200">
              <span className="font-mono-plex text-[10px] font-semibold text-zinc-500 block mb-1.5">
                REASONING
              </span>
              <p className="text-[12.5px] text-zinc-700 leading-relaxed">
                {claim.detailed_reasoning}
              </p>
            </div>
          )}
        </motion.div>
      )}
    </motion.article>
  );
}
